"use client";

import * as React from "react";
import Link from "next/link";
import { Sparkles, ImagePlus, Wand2, Copy, Check, Eye } from "lucide-react";
import { Modal } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { ResourceDTO } from "@/types/resource";

type Filter = "all" | "image" | "text";

/** Kho Prompt: bộ lọc theo đích (ảnh / trợ lý) + lưới thẻ prompt. */
export function PromptLibrary({ items }: { items: ResourceDTO[] }) {
  const [filter, setFilter] = React.useState<Filter>("all");

  const counts = {
    all: items.length,
    image: items.filter((i) => i.promptTarget === "image").length,
    text: items.filter((i) => i.promptTarget === "text").length,
  };
  const shown = filter === "all" ? items : items.filter((i) => i.promptTarget === filter);

  const TABS: { key: Filter; label: string; icon: typeof Wand2 }[] = [
    { key: "all", label: `Tất cả (${counts.all})`, icon: Wand2 },
    { key: "image", label: `Tạo ảnh (${counts.image})`, icon: ImagePlus },
    { key: "text", label: `Trợ lý AI (${counts.text})`, icon: Sparkles },
  ];

  return (
    <div className="space-y-6">
      {/* Bộ lọc */}
      <div className="flex flex-wrap justify-center gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = filter === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-body-sm font-semibold transition-colors",
                active
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-border bg-surface text-foreground-secondary hover:border-primary/50 hover:text-primary"
              )}
            >
              <Icon className="size-4" aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </div>

      {shown.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-surface py-12 text-center text-body-sm text-muted">
          Chưa có prompt nào trong nhóm này.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((r) => (
            <PromptCard key={r.id} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function PromptCard({ r }: { r: ResourceDTO }) {
  const [copied, setCopied] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const isImage = r.promptTarget === "image";
  const targetPath = isImage ? "/ai-ho-tro/tao-anh-video" : "/ai-ho-tro/tro-ly-ai";
  const useHref = `${targetPath}?prompt=${encodeURIComponent(r.prompt)}`;
  const isLong = r.prompt.length > 140 || r.prompt.includes("\n");

  async function copy() {
    try {
      await navigator.clipboard.writeText(r.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* trình duyệt chặn clipboard */
    }
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-sm transition-all duration-fast ease-out hover:-translate-y-1 hover:shadow-lg">
      {/* Ảnh kết quả */}
      <div className="relative aspect-video w-full overflow-hidden bg-subtle">
        {r.previewUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={r.previewUrl} alt={r.title} loading="lazy" className="size-full object-cover" />
        ) : (
          <div className="grid size-full place-items-center bg-gradient-to-br from-amber-300 to-orange-400 text-white">
            <Wand2 className="size-9" aria-hidden="true" />
          </div>
        )}
        <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-caption font-semibold text-white backdrop-blur">
          {isImage ? <ImagePlus className="size-3.5" aria-hidden="true" /> : <Sparkles className="size-3.5" aria-hidden="true" />}
          {isImage ? "Tạo ảnh" : "Trợ lý AI"}
        </span>
      </div>

      {/* Nội dung */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <h3 className="line-clamp-1 text-body font-bold text-foreground">{r.title}</h3>

        <div className="rounded-lg border border-border bg-subtle/60 p-3">
          <p className="line-clamp-4 whitespace-pre-line text-caption leading-relaxed text-foreground-secondary">
            {r.prompt}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-1 inline-flex items-center gap-1 text-caption font-medium text-primary hover:underline"
            >
              <Eye className="size-3.5" aria-hidden="true" />
              Xem chi tiết
            </button>
          )}
        </div>

        <div className="mt-auto flex gap-2">
          <Link
            href={useHref}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 px-3 py-2 text-caption font-semibold text-white transition-opacity hover:opacity-90"
          >
            <Sparkles className="size-3.5" aria-hidden="true" />
            Dùng prompt với AI
          </Link>
          <button
            type="button"
            onClick={copy}
            title="Chép prompt"
            aria-label="Chép prompt"
            className="inline-flex shrink-0 items-center justify-center rounded-full border border-border-strong px-3 text-foreground-secondary hover:bg-subtle"
          >
            {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
          </button>
        </div>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="md"
        title={r.title}
        description={isImage ? "Prompt tạo ảnh" : "Prompt Trợ lý AI"}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-full border border-border-strong px-4 py-2 text-body-sm font-medium text-foreground-secondary hover:bg-subtle"
            >
              {copied ? <Check className="size-4 text-success" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
              {copied ? "Đã chép" : "Sao chép"}
            </button>
            <Link
              href={useHref}
              className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 px-4 py-2 text-body-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <Sparkles className="size-4" aria-hidden="true" />
              Dùng prompt với AI
            </Link>
          </div>
        }
      >
        <div className="max-h-[60vh] overflow-y-auto whitespace-pre-line break-words rounded-xl bg-subtle px-4 py-3 text-body-sm leading-relaxed text-foreground">
          {r.prompt}
        </div>
      </Modal>
    </div>
  );
}
